import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { AxiosError } from "axios";
import { Button, Dialog, TextInput } from "fe-shared/src/components";
import { CheckIcon, EditIcon } from "fe-shared/src/components/Icons";
import { ApiError } from "fe-shared/src/types/common";
import useDialog from "fe-shared/src/hooks/useDialog";
import * as api from "./modules/api";

interface Props {
  transactionId: number;
  currentClaimNumber?: string | null;
}

const ClaimNumberEditButton = ({ transactionId, currentClaimNumber }: Props) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(currentClaimNumber ?? "");
  const { isDialogRendered, isDialogActive, openDialog, closeDialog } = useDialog();

  const [updateClaimNumber, { isLoading }] = useMutation<
    unknown,
    AxiosError<ApiError>,
    api.UpdateClaimNumberParams
  >(api.updateClaimNumber, {
    throwOnError: true,
    onSuccess: () => {
      document.location.reload();
      closeDialog();
    },
  });

  const isValueValid = value.length <= 7;

  const actions: React.ReactNode[] = [
    <Button
      key="submit"
      id="claim-number-submit"
      variant="filled"
      iconComponent={CheckIcon}
      onClick={() => isValueValid && updateClaimNumber({ transactionId, claimNumber: value })}
      disabled={isLoading || !isValueValid}
    >
      {t("submit")}
    </Button>,
    <Button key="cancel" id="claim-number-cancel" variant="outlined" onClick={closeDialog}>
      {t("cancel")}
    </Button>,
  ];

  return (
    <>
      <Button
        id="claim-number-edit-button"
        variant="outlined"
        iconComponent={EditIcon}
        onClick={openDialog}
      >
        {t("transactions.details.claimNumber.editButton")}
      </Button>
      {isDialogRendered && (
        <Dialog
          modal
          isActive={isDialogActive}
          onClose={closeDialog}
          title={t("transactions.details.claimNumber.dialogTitle")}
          actions={actions}
        >
          <TextInput
            id="claim-number-input"
            title={t("transactions.details.claimNumber.label")}
            placeholder={t("transactions.details.claimNumber.placeholder")}
            value={value}
            isError={!isValueValid}
            errorText={!isValueValid ? t("validation.maxLength", { max: 7 }) : undefined}
            onChange={(_, v) => setValue(v)}
          />
        </Dialog>
      )}
    </>
  );
};

export default ClaimNumberEditButton;
